import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/api';
import { BUSINESS_NAME, TERMS_URL } from '../../config/brand';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, ArrowRight, Check, Upload, X, FileText, Camera, Shield, Heart, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const defaultPet = {
  name: '', species: 'dog', breed: '', gender: '', date_of_birth: '',
  weight: '', color_coat: '', spayed_neutered: false, microchip_info: '',
  photo_url: '', photo_file_ids: [], vaccination_file_ids: [],
  medical_conditions: '', allergies: '', medications: '',
  vet_name: '', vet_phone: '', emergency_contact: '',
  behavioral_notes: '', aggression_triggers: '', grooming_preferences: '',
  special_handling: '', consent_given: false,
  owner_name: '', owner_phone: '', owner_address: '',
};

const STEPS = [
  { label: 'Basic Info', icon: Heart },
  { label: 'Vaccination', icon: Shield },
  { label: 'Owner Details', icon: FileText },
  { label: 'Behaviour & Medical', icon: ClipboardCheck },
  { label: 'Terms & Conditions', icon: Check },
];

function FileUploader({ label, fileIds, onUpload, onRemove, accept = "image/*" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({});

  useEffect(() => {
    fileIds.forEach(fid => {
      if (!previews[fid]) {
        const token = localStorage.getItem('zeezo_token');
        api.get(`/files/download/${fid}`, { responseType: 'blob' })
          .then(res => {
            const url = URL.createObjectURL(res.data);
            setPreviews(prev => ({ ...prev, [fid]: url }));
          })
          .catch(() => {});
      }
    });
  }, [fileIds]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        onUpload(res.data.file_id);
        const url = URL.createObjectURL(file);
        setPreviews(prev => ({ ...prev, [res.data.file_id]: url }));
      } catch (err) {
        toast.error(`Upload failed: ${err.response?.data?.detail || 'Unknown error'}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <Label className="text-sm font-medium mb-2 block">{label}</Label>
      <div className="flex flex-wrap gap-3 mb-3">
        {fileIds.map(fid => (
          <div key={fid} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#E8DDD4] bg-[#F5EDE6]">
            {previews[fid] ? (
              <img src={previews[fid]} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#A09387]" />
              </div>
            )}
            <button
              type="button"
              onClick={() => onRemove(fid)}
              className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-20 h-20 rounded-xl border-2 border-dashed border-[#C48C68] bg-[#FBF7F3] flex flex-col items-center justify-center hover:bg-[#F5EDE6] transition-colors"
          data-testid={`upload-${label.toLowerCase().replace(/\s/g, '-')}-btn`}
        >
          {uploading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#6A3E28] border-t-transparent" />
          ) : (
            <>
              <Upload className="w-5 h-5 text-[#A96B49]" />
              <span className="text-[10px] text-[#A96B49] mt-1">Upload</span>
            </>
          )}
        </button>
      </div>
      <input ref={inputRef} type="file" accept={accept} multiple onChange={handleFiles} className="hidden" />
    </div>
  );
}

export default function PetForm() {
  const navigate = useNavigate();
  const { petId } = useParams();
  const isEdit = !!petId;
  const [form, setForm] = useState(defaultPet);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [vaccinations, setVaccinations] = useState([]);
  const [vaccForm, setVaccForm] = useState({ vaccine_name: '', date_administered: '', expiry_date: '', rabies_status: '', notes: '' });
  const [breeds, setBreeds] = useState([]);

  useEffect(() => {
    api.get('/pricing/breeds').then(r => setBreeds(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/pets/${petId}`).then(r => {
        const pet = r.data;
        setForm({
          ...defaultPet, ...pet, weight: pet.weight || '',
          medical_conditions: (pet.medical_conditions || []).join(', '),
          allergies: (pet.allergies || []).join(', '),
          medications: (pet.medications || []).join(', '),
        });
      }).catch(() => toast.error('Pet not found'));
      api.get(`/vaccinations/${petId}`).then(r => setVaccinations(r.data)).catch(() => {});
    }
  }, [petId, isEdit]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const handleSelect = (field) => (val) => setForm({ ...form, [field]: val });
  const handleSwitch = (field) => (checked) => setForm({ ...form, [field]: checked });
  const toArray = (str) => (str || '').split(',').map(s => s.trim()).filter(Boolean);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Pet name is required'); return; }
    if (!form.consent_given) { toast.error('Please accept the Terms & Conditions'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form, weight: form.weight ? parseFloat(form.weight) : null,
        medical_conditions: toArray(form.medical_conditions),
        allergies: toArray(form.allergies),
        medications: toArray(form.medications),
      };
      if (isEdit) {
        await api.put(`/pets/${petId}`, payload);
        toast.success('Pet updated');
      } else {
        await api.post('/pets', payload);
        toast.success('Pet added');
      }
      navigate('/client/pets');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const addVaccination = async () => {
    if (!vaccForm.vaccine_name) { toast.error('Vaccine name required'); return; }
    if (!isEdit) { toast.info('Save the pet first, then add vaccinations'); return; }
    try {
      const res = await api.post('/vaccinations', { ...vaccForm, pet_id: petId });
      setVaccinations([...vaccinations, res.data]);
      setVaccForm({ vaccine_name: '', date_administered: '', expiry_date: '', rabies_status: '', notes: '' });
      toast.success('Vaccination added');
    } catch {
      toast.error('Failed to add vaccination');
    }
  };

  const deleteVacc = async (id) => {
    await api.delete(`/vaccinations/${id}`);
    setVaccinations(vaccinations.filter(v => v.vaccination_id !== id));
    toast.success('Vaccination removed');
  };

  const addPhotoFile = (fileId) => setForm(f => ({ ...f, photo_file_ids: [...f.photo_file_ids, fileId] }));
  const removePhotoFile = (fileId) => setForm(f => ({ ...f, photo_file_ids: f.photo_file_ids.filter(x => x !== fileId) }));
  const addVaccFile = (fileId) => setForm(f => ({ ...f, vaccination_file_ids: [...f.vaccination_file_ids, fileId] }));
  const removeVaccFile = (fileId) => setForm(f => ({ ...f, vaccination_file_ids: f.vaccination_file_ids.filter(x => x !== fileId) }));

  const canProceed = () => {
    if (step === 0) return !!form.name.trim();
    if (step === 4) return form.consent_given;
    return true;
  };

  const handleNext = () => {
    if (step === 0 && !form.name.trim()) { toast.error('Pet name is required'); return; }
    if (step < 4) setStep(step + 1);
    else handleSubmit();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="pet-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => step > 0 ? setStep(step - 1) : navigate('/client/pets')} className="rounded-full" data-testid="back-to-pets-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>{isEdit ? 'Edit Pet' : 'Add New Pet'}</h1>
          <p className="text-sm text-[#7A6B5E]">Step {step + 1} of {STEPS.length}: {STEPS[step].label}</p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-2 flex-1">
            <div
              className={`step-dot cursor-pointer ${i < step ? 'completed' : i === step ? 'active' : ''}`}
              onClick={() => { if (i < step) setStep(i); }}
            >
              {i < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
            </div>
            {i < STEPS.length - 1 && <div className={`step-line ${i < step ? 'completed' : ''}`} />}
          </div>
        ))}
      </div>

      {/* Step 0: Basic Info */}
      {step === 0 && (
        <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
          <CardContent className="p-6 space-y-5">
            <h3 className="text-lg font-semibold text-[#3D2418]">Basic Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm">Pet Name *</Label><Input value={form.name} onChange={handleChange('name')} className="mt-1 rounded-lg" required data-testid="pet-name-input" /></div>
              <div><Label className="text-sm">Species</Label>
                <Select value={form.species} onValueChange={handleSelect('species')}><SelectTrigger className="mt-1 rounded-lg" data-testid="pet-species-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="dog">Dog</SelectItem><SelectItem value="cat">Cat</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-sm">Breed</Label>
                <Select value={form.breed} onValueChange={handleSelect('breed')}><SelectTrigger className="mt-1 rounded-lg" data-testid="pet-breed-input"><SelectValue placeholder="Select breed" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {breeds.filter(b => b.species === form.species).map(b => (
                    <SelectItem key={b.breed_id} value={b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent></Select>
              </div>
              <div><Label className="text-sm">Gender</Label>
                <Select value={form.gender} onValueChange={handleSelect('gender')}><SelectTrigger className="mt-1 rounded-lg" data-testid="pet-gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-sm">Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={handleChange('date_of_birth')} className="mt-1 rounded-lg" data-testid="pet-dob-input" /></div>
              <div><Label className="text-sm">Weight (kg)</Label><Input type="number" step="0.1" value={form.weight} onChange={handleChange('weight')} className="mt-1 rounded-lg" data-testid="pet-weight-input" /></div>
              <div><Label className="text-sm">Color / Coat Type</Label><Input value={form.color_coat} onChange={handleChange('color_coat')} className="mt-1 rounded-lg" data-testid="pet-coat-input" /></div>
              <div><Label className="text-sm">Microchip Info</Label><Input value={form.microchip_info} onChange={handleChange('microchip_info')} className="mt-1 rounded-lg" data-testid="pet-microchip-input" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.spayed_neutered} onCheckedChange={handleSwitch('spayed_neutered')} data-testid="pet-spayed-switch" />
              <Label className="text-sm">Spayed / Neutered</Label>
            </div>
            <FileUploader
              label="Pet Photos"
              fileIds={form.photo_file_ids}
              onUpload={addPhotoFile}
              onRemove={removePhotoFile}
              accept="image/jpeg,image/png,image/webp"
            />
          </CardContent>
        </Card>
      )}

      {/* Step 1: Vaccination Details */}
      {step === 1 && (
        <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
          <CardContent className="p-6 space-y-5">
            <h3 className="text-lg font-semibold text-[#3D2418]">Vaccination Details</h3>
            
            <FileUploader
              label="Vaccination Certificates / Records"
              fileIds={form.vaccination_file_ids}
              onUpload={addVaccFile}
              onRemove={removeVaccFile}
              accept="image/jpeg,image/png,image/webp,application/pdf"
            />

            {isEdit && (
              <>
                <div className="border-t border-[#E8DDD4] pt-4">
                  <h4 className="text-sm font-semibold text-[#3D2418] mb-3">Vaccination Records</h4>
                  {vaccinations.map(v => (
                    <div key={v.vaccination_id} className="flex items-center justify-between bg-[#FBF7F3] p-3 rounded-xl mb-2">
                      <div>
                        <p className="text-sm font-medium text-[#3D2418]">{v.vaccine_name}</p>
                        <p className="text-xs text-[#A09387]">Given: {v.date_administered} | Expires: {v.expiry_date || 'N/A'}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => deleteVacc(v.vaccination_id)}>Remove</Button>
                    </div>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-[#E8DDD4]">
                  <Input placeholder="Vaccine Name" value={vaccForm.vaccine_name} onChange={(e) => setVaccForm({ ...vaccForm, vaccine_name: e.target.value })} className="rounded-lg" data-testid="vacc-name-input" />
                  <Input type="date" value={vaccForm.date_administered} onChange={(e) => setVaccForm({ ...vaccForm, date_administered: e.target.value })} className="rounded-lg" data-testid="vacc-date-input" />
                  <Input type="date" placeholder="Expiry" value={vaccForm.expiry_date} onChange={(e) => setVaccForm({ ...vaccForm, expiry_date: e.target.value })} className="rounded-lg" data-testid="vacc-expiry-input" />
                  <Button type="button" variant="outline" className="rounded-lg border-[#C48C68] text-[#6A3E28]" onClick={addVaccination} data-testid="add-vacc-btn">Add Vaccination</Button>
                </div>
              </>
            )}
            {!isEdit && (
              <p className="text-sm text-[#A09387] italic">Save the pet first to add detailed vaccination records. You can upload vaccination certificates now.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Owner Details */}
      {step === 2 && (
        <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
          <CardContent className="p-6 space-y-5">
            <h3 className="text-lg font-semibold text-[#3D2418]">Owner Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm">Owner Name</Label><Input value={form.owner_name} onChange={handleChange('owner_name')} placeholder="Your full name" className="mt-1 rounded-lg" data-testid="owner-name-input" /></div>
              <div><Label className="text-sm">Owner Phone</Label><Input value={form.owner_phone} onChange={handleChange('owner_phone')} placeholder="Phone number" className="mt-1 rounded-lg" data-testid="owner-phone-input" /></div>
              <div className="sm:col-span-2"><Label className="text-sm">Address</Label><Textarea value={form.owner_address} onChange={handleChange('owner_address')} placeholder="Home address" className="mt-1 rounded-lg" rows={2} data-testid="owner-address-input" /></div>
              <div><Label className="text-sm">Vet Name</Label><Input value={form.vet_name} onChange={handleChange('vet_name')} className="mt-1 rounded-lg" data-testid="pet-vet-input" /></div>
              <div><Label className="text-sm">Vet Phone</Label><Input value={form.vet_phone} onChange={handleChange('vet_phone')} className="mt-1 rounded-lg" data-testid="pet-vetphone-input" /></div>
              <div><Label className="text-sm">Emergency Contact</Label><Input value={form.emergency_contact} onChange={handleChange('emergency_contact')} className="mt-1 rounded-lg" data-testid="pet-emergency-input" /></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Behaviour & Medical */}
      {step === 3 && (
        <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
          <CardContent className="p-6 space-y-5">
            <h3 className="text-lg font-semibold text-[#3D2418]">Behaviour & Medical Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm">Medical Conditions (comma separated)</Label><Input value={form.medical_conditions} onChange={handleChange('medical_conditions')} className="mt-1 rounded-lg" data-testid="pet-medical-input" /></div>
              <div><Label className="text-sm">Allergies (comma separated)</Label><Input value={form.allergies} onChange={handleChange('allergies')} className="mt-1 rounded-lg" data-testid="pet-allergies-input" /></div>
              <div><Label className="text-sm">Medications (comma separated)</Label><Input value={form.medications} onChange={handleChange('medications')} className="mt-1 rounded-lg" data-testid="pet-medications-input" /></div>
            </div>
            <div className="space-y-4">
              <div><Label className="text-sm">Behavioral Notes</Label><Textarea value={form.behavioral_notes} onChange={handleChange('behavioral_notes')} className="mt-1 rounded-lg" rows={2} data-testid="pet-behavior-input" /></div>
              <div><Label className="text-sm">Aggression / Anxiety Triggers</Label><Textarea value={form.aggression_triggers} onChange={handleChange('aggression_triggers')} className="mt-1 rounded-lg" rows={2} data-testid="pet-triggers-input" /></div>
              <div><Label className="text-sm">Grooming Preferences</Label><Textarea value={form.grooming_preferences} onChange={handleChange('grooming_preferences')} className="mt-1 rounded-lg" rows={2} data-testid="pet-grooming-pref-input" /></div>
              <div><Label className="text-sm">Special Handling Instructions</Label><Textarea value={form.special_handling} onChange={handleChange('special_handling')} className="mt-1 rounded-lg" rows={2} data-testid="pet-handling-input" /></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Terms & Conditions */}
      {step === 4 && (
        <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
          <CardContent className="p-6 space-y-5">
            <h3 className="text-lg font-semibold text-[#3D2418]">Terms & Conditions</h3>
            <div className="bg-[#FBF7F3] rounded-xl p-5 text-sm text-[#5A4E44] space-y-3 max-h-64 overflow-y-auto">
              <p>By proceeding, you agree to the following:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>I confirm all information provided about my pet is accurate and complete.</li>
                <li>I understand that grooming involves physical handling and I consent to the grooming procedures.</li>
                <li>I have disclosed all medical conditions, allergies, and behavioral issues.</li>
                <li>I agree to the cancellation and refund policy as outlined in the service agreement.</li>
                <li>I understand that {BUSINESS_NAME} is not liable for pre-existing conditions that may be aggravated during grooming.</li>
                <li>I consent to my pet being photographed for record-keeping purposes.</li>
              </ul>
              <p>
                For full details, please read our{' '}
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#6A3E28] underline font-semibold hover:text-[#A96B49]"
                  data-testid="terms-link"
                >
                  Terms & Conditions
                </a>.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Switch checked={form.consent_given} onCheckedChange={handleSwitch('consent_given')} data-testid="pet-consent-switch" />
              <Label className="text-sm">
                I have read and agree to the{' '}
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-[#6A3E28] underline font-semibold">Terms & Conditions</a>
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Spacer */}
      <div className="h-20" />

      {/* Sticky Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8DDD4] px-6 py-3 flex justify-between z-[60] mb-[60px] md:mb-0" style={{ boxShadow: '0 -4px 16px rgba(106,62,40,0.06)' }} data-testid="pet-form-sticky-bar">
        <Button variant="outline" className="rounded-full border-[#C48C68] text-[#6A3E28]" onClick={() => step > 0 ? setStep(step - 1) : navigate('/client/pets')} data-testid="pet-form-prev-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!canProceed() || loading}
          className="rounded-full bg-[#6A3E28] text-white hover:bg-[#5A3220] shadow-md px-8"
          data-testid={step < 4 ? 'pet-form-next-btn' : 'save-pet-btn'}
        >
          {step < 4 ? (
            <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>
          ) : loading ? 'Saving...' : isEdit ? 'Update Pet' : 'Add Pet'}
        </Button>
      </div>
    </div>
  );
}
